import React, { createContext, useCallback, useContext, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useIntl } from 'react-intl'

export interface ConfirmOptions {
  title?: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'primary' | 'warning'
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions>({})
  const [loading, setLoading] = useState(false)
  const [resolver, setResolver] = useState<((v: boolean) => void) | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve)
    })
  }, [])

  const handleClose = (result: boolean) => {
    setOpen(false)
    setLoading(false)
    setOptions({})
    if (resolver) {
      resolver(result)
      setResolver(null)
    }
  }

  const handleConfirm = () => {
    setLoading(true)
    handleClose(true)
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Modal
        show={open}
        onHide={() => !loading && handleClose(false)}
        centered
        contentClassName='rounded-4 shadow'
      >
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='fs-4 fw-bold'>
            {options.title || intl.formatMessage({ id: 'CONFIRM.DELETE.TITLE' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-2 text-gray-700'>
          {options.message || intl.formatMessage({ id: 'CONFIRM.DELETE.BODY' })}
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0'>
          <Button
            variant='light'
            className='rounded-pill px-4'
            onClick={() => handleClose(false)}
            disabled={loading}
          >
            {options.cancelLabel || intl.formatMessage({ id: 'ACTION.CANCEL' })}
          </Button>
          <Button
            variant={options.variant || 'danger'}
            className='rounded-pill px-4'
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Spinner size='sm' animation='border' className='me-2' />}
            {options.confirmLabel || intl.formatMessage({ id: 'ACTION.DELETE' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmContextValue['confirm'] {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    // Fallback: si pas de provider, on retourne une fonction qui confirme toujours
    return async () => true
  }
  return ctx.confirm
}

export default ConfirmProvider
